/**
 * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (EPIC-019 S-C,
 * 2026-08-21) — de **enige** plek in de FE waar de verzekeraar-gebonden-
 * hersteller-capability wordt geïnterpreteerd.
 *
 * ## Waarom één module
 * S-D..S-I hangen elk nog een gate aan deze capability. Zeven stories × één
 * inline `user?.restrictedRepairer?.active === true` = zeven subtiel divergerende
 * gates. Daarom: **nergens anders in `apps/web/` staat een inline
 * `restrictedRepairer`-vergelijking**; consumers importeren deze helpers en geven
 * hoogstens de resulterende boolean door. Geborgd met een scan-test
 * (`restrictedRepairer.scan.test.ts`), gespiegeld op de borg van
 * `features/dossiers/repairerGate.ts`.
 *
 * ## KERNCONDITIE (gebruiker, 2026-08-21)
 * Op de originele CARculate-instantie zitten **naast** de beperkte herstellers
 * ook **gewone** herstellers. De omgevings-schakelaar "aan" betekent dus **niet**
 * dat iedereen beperkt is. Er staat hier daarom **geen** conditie op de
 * omgevings-schakelaar, op de instantie of op een keten — uitsluitend op de
 * capability van de **eigen sessie**.
 *
 * ## Waarom `resolved` NIET gelezen wordt (bindende ontwerpregel a)
 * `resolved: false` betekent **ONBEKEND** (mislukte schakelaar- of
 * per-hersteller-read; fundering D1 + C1), niet "beperkt". De FE verbergt
 * **uitsluitend** bij `active === true`. Zou hij bij "onbekend" verbergen, dan
 * verdwijnen bij een DB-hikje tabs en menu's op instanties waar de regeling niet
 * eens bestaat. De **echte** handhaving is de BE-poort — die doet bij "onbekend"
 * wél een gerichte her-lezing.
 *
 * ## Vorm
 * Puur en `unknown`-tolerant (spiegelt `repairerGate.ts`): geen React-import,
 * geen `useAuth`, geen module-level state/cache, geen side-effects, gooit nooit.
 * Alleen de **strikte** boolean `true` telt (`=== true`, nooit `!!`, nooit
 * `== true`) — een string `'true'`, `1` of een truthy object is géén `true`.
 */

/**
 * Interne, defensieve kern: pak `restrictedRepairer` uit een willekeurige waarde,
 * of `null` als er niets bruikbaars staat. Accepteert `unknown` zodat een
 * niet-getypte whoami-respons (oude mock, handmatige fixture, JSON van een
 * oudere BE) niet stiekem door een cast glipt.
 */
function readCapability(user: unknown): Record<string, unknown> | null {
    if (typeof user !== 'object' || user === null) return null;
    const raw = (user as Record<string, unknown>).restrictedRepairer;
    // `typeof null === 'object'` en arrays zijn ook objecten → beide uitsluiten.
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
    return raw as Record<string, unknown>;
}

/**
 * Of DEZE sessie een **verzekeraar-gebonden (beperkte) hersteller** is.
 *
 * `true` **dan en slechts dan als** `user.restrictedRepairer.active === true`
 * (AC 3). Alle andere vormen — veld afwezig, `null`, `{}`, een string, `active:
 * 'true'`, `active: 1`, `user` zelf `null` — leveren `false` (AC 5).
 *
 * Dit is het enige signaal waarop de FE iets verbergt.
 */
export function isRestrictedRepairerActive(user: unknown): boolean {
    return readCapability(user)?.active === true;
}

/**
 * **Mag deze sessie de VERZEKERAAR van een dossier wijzigen — en zo niet,
 * waarom niet?**
 *
 * Story `fe-be-verzekeraar-zoeken-op-groenekaartcode` (D6 / review-bevinding
 * R-04, 13-09-2026). Een aparte, **fail-closed** vraag — bewust géén wijziging
 * aan {@link isRestrictedRepairerActive} en dus geen wijziging van bindende
 * ontwerpregel a.
 *
 * ## Waarom dit een tweede helper is en niet dezelfde
 * {@link isRestrictedRepairerActive} beantwoordt: *"moet ik hier iets
 * VERBERGEN?"* — en daar is "onbekend" bewust geen grond voor, anders
 * verdwijnen bij een DB-hikje tabs en menu's op instanties waar de regeling niet
 * eens bestaat. Deze helper beantwoordt een andere vraag: *"mag ik de gebruiker
 * een schakelaar aanbieden die een verzekeraar-WIJZIGING verstuurt?"* — en daar
 * is "onbekend" wél een grond om nee te zeggen, omdat de **backend-poort
 * strenger is dan de FE-vlag**: bij `EnforcementUnknown` (capability onbekend én
 * de gerichte her-lezing bleef onbekend) antwoordt
 * `RestrictedRepairerInsurerGate.EvaluatePolicyPatch` met `DenyInsurer`.
 *
 * ## Wat er misgaat zonder deze helper
 * De groene-kaart-popup stuurt **één PATCH per endpoint**. Staat de
 * verzekeraar-rij open in de onbekend-tak, dan vertrekt er één polis-PATCH met
 * verzekeraar **én** polisnummer **én** "geldig tot", geeft de backend
 * **403 `restricted_repairer_insurer_locked`**, en vallen die buurvelden in
 * dezelfde call mee — terwijl de gebruiker dacht dat hij ze had overgenomen. Dat
 * is exact het `policyValidFrom`-defect van 12-09-2026 met een andere afzender.
 *
 * ## De drie standen
 * | capability | uitkomst |
 * |---|---|
 * | afwezig / geen object | `'open'` — betrouwbaar **niet** gebonden (de BE laat het veld juist wég als dat vaststaat) |
 * | `{ active: true, … }` | `'fixed'` — gebonden aan één maatschappij |
 * | `{ active: false, resolved: false }` | **`'unknown'`** — niet vast te stellen ⇒ fail-closed |
 * | `{ active: false, resolved: true }` | `'open'` — betrouwbaar niet gebonden |
 *
 * ⚠⚠ **`'fixed'` en `'unknown'` zetten allebei dicht en zijn tóch niet
 * hetzelfde.** Ze samenvouwen tot één boolean lijkt goedkoper en levert een
 * scherm op dat bij `'unknown'` zegt *"vaste verzekeraar"* — een bewering die
 * dáár niet waar is. Het dichtzetten is juist; de motivering moet het óók zijn.
 * De teksten staan in `features/dossiers/lib/insurerFieldLock.ts`.
 *
 * ⚠ Een veld dat onterecht op slot staat is zichtbaar en te verhelpen; een
 * polisnummer dat stil niet is overgenomen niet.
 *
 * ⚠ De union staat hier **structureel** (geen import): deze module importeert
 * bewust niets, en een import de andere kant op zou het capability-token in elk
 * consumerbestand zetten.
 *
 * Puur en `unknown`-tolerant, gooit nooit — zelfde vorm als de helpers hierboven.
 */
export function resolveInsurerWriteGate(user: unknown): 'open' | 'fixed' | 'unknown' {
  const cap = readCapability(user);
  // Geen capability = de backend heeft vastgesteld dat er niets te handhaven
  // valt (het veld is dan bewust afwezig). Niets dichtzetten.
  if (cap === null) return 'open';
  if (cap.active === true) return 'fixed';
  // De capability IS er, maar is niet betrouwbaar vastgesteld → de BE-poort zou
  // een verzekeraar-wijziging weigeren, dus bieden we hem niet aan. Wél met een
  // eigen reden: er is hier geen vaste verzekeraar, alleen een onbekende
  // handhavingstoestand.
  return cap.resolved === true ? 'open' : 'unknown';
}

/**
 * Of het **LOSSE kladblok** (`/calculatie`, het Glas-menu-item) beschikbaar is.
 *
 * Let op de bewust contra-intuïtieve regel (AC 3, AC 8): voor een **niet-beperkte**
 * hersteller heeft de vlag geen betekenis → het kladblok is gewoon beschikbaar,
 * dus `true`. Alleen bij een **beperkte** hersteller beslist
 * `mayUseScratchpad === true`.
 *
 * Gevolg-invariant (getest): `mayUseScratchpad(u) === false` impliceert altijd
 * `isRestrictedRepairerActive(u) === true`. Het kladblok kan dus **nooit**
 * verdwijnen bij een niet-beperkte gebruiker.
 *
 * NB: dit gaat **niet** over de Calculatie-**tab** in het dossier. Die blijft
 * altijd staan, óók zonder deze vlag (AC 15, epic-beslissing 5).
 */
export function mayUseScratchpad(user: unknown): boolean {
    const cap = readCapability(user);
    if (cap?.active !== true) return true;
    return cap.mayUseScratchpad === true;
}

/**
 * De **naam van de vaste verzekeraar** van een beperkte hersteller, of `null`.
 *
 * Story `fe-be-verzekeraar-gebonden-hersteller-dashboard` (EPIC-019 S-D,
 * 2026-08-21): het eenvoudige dashboard zet onder de begroeting de regel
 * **"Dossiers voor «verzekeraarsnaam»"**.
 *
 * ## Regels
 * - **Alleen bij een beperkte sessie**: bij `active !== true` levert de helper
 *   `null`. Buiten de beperkte context heeft die naam geen betekenis, en een
 *   niet-null-retour is een uitnodiging om hem elders te tonen.
 * - **Niet-lege string, getrimd.** `null`, `''`, `'   '` en een niet-string
 *   (getal, object) leveren `null` — dan wordt de sub-regel **weggelaten**
 *   (geen lege "Dossiers voor"), want dit is de inrichtingsfout uit AC 17 van de
 *   fundering.
 * - **NOOIT een GUID in de UI.** De helper geeft uitsluitend
 *   `insuranceCompanyName` terug; `insuranceCompanyId` wordt hier bewust NIET
 *   als fallback gebruikt.
 * - Puur en `unknown`-tolerant, gooit nooit — zelfde vorm als de twee helpers
 *   hierboven.
 */
export function restrictedInsurerName(user: unknown): string | null {
    const cap = readCapability(user);
    if (cap?.active !== true) return null;
    const raw = cap.insuranceCompanyName;
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return trimmed === '' ? null : trimmed;
}

/**
 * De **id** van de vaste verzekeraar van een beperkte hersteller, of `null`.
 *
 * Story `fe-be-verzekeraar-gebonden-hersteller-dashboard` (EPIC-019 S-D): de vijf
 * tegels klikken door naar de dossierlijst met `?insuranceCompanyId=<id>`, zodat
 * de doorklik-lijst **dezelfde set** selecteert als de tegel-count. Zonder die
 * param zou de tegel 41 tonen naast een lijst van alle verzekeraars — precies de
 * verwarring die deze story moet voorkomen.
 *
 * ## Dit is GEEN scope-lek
 * De id is een **filter-param**, geen poort. De BE-count blijft op de sessie
 * gescoopt (die leest de verzekeraar uitsluitend uit `SessionRecord`, nooit uit
 * de query); de lijst blijft op de bestaande access-scoping gescoopt. De param
 * kan de set dus alleen VERKLEINEN — en de gebruiker mag hem in de filterbalk
 * weghalen om z'n oude dossiers bij een andere verzekeraar te zien (concept
 * Deel 5).
 *
 * Alleen bij `active === true`, en alleen een niet-lege string. Een niet-string of
 * lege waarde levert `null` → de href gaat dan zonder insurer-param mee (de
 * fail-closed BE-count is dan toch 0, dus er valt niets te matchen).
 */
export function restrictedInsurerId(user: unknown): string | null {
    const cap = readCapability(user);
    if (cap?.active !== true) return null;
    const raw = cap.insuranceCompanyId;
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return trimmed === '' ? null : trimmed;
}

/**
 * De leesbare naam voor een verzekeraar-**id** dat op de dossierlijst gefilterd
 * wordt — maar **alleen** wanneer dat de vaste verzekeraar van DEZE beperkte
 * sessie is. Anders `null`.
 *
 * Story `fe-be-verzekeraar-gebonden-hersteller-dashboard` (EPIC-019 S-D): na een
 * doorklik vanaf een tegel staat er een wegklikbare filter-chip. Die moet de
 * **naam** tonen, nooit de ruwe GUID (AC 30). Er is (nog) geen
 * verzekeraar-optielijst op het zoekscherm, en de sessie-capability is de enige
 * plek waar de naam al gratis beschikbaar is — dus doet deze helper de koppeling.
 *
 * ## Waarom de id-vergelijking hier hoort
 * De vergelijking leest `insuranceCompanyId`, en dat mag **uitsluitend** in deze
 * gate-module (de scan-test verbiedt het elders sinds S-D). Zonder de
 * vergelijking zou een handmatig aangepaste deep-link met een ándere
 * verzekeraar-id de naam van de eigen verzekeraar in de chip zetten — een chip
 * die dan iets anders belooft dan de lijst toont. Vergelijking is
 * case-insensitief en accolade-tolerant (dezelfde GUID in een andere notatie is
 * dezelfde verzekeraar).
 */
export function restrictedInsurerLabelFor(user: unknown, insurerId: unknown): string | null {
    if (typeof insurerId !== 'string') return null;
    const cap = readCapability(user);
    if (cap?.active !== true) return null;

    const own = cap.insuranceCompanyId;
    if (typeof own !== 'string') return null;

    const normalise = (s: string) => s.trim().replace(/[{}]/g, '').toLowerCase();
    if (normalise(own) === '' || normalise(own) !== normalise(insurerId)) return null;

    return restrictedInsurerName(user);
}
